import type {
  TwoFAParams,
  WithdrawCommissionData,
  WithdrawSccrsBodyRequest,
  WithdrawSccrsErrorResponse,
  WithdrawSuccessResponse,
} from '@/shared/types';
import {
  useWithdrawSccrs,
  useWithdrawSccrsModals,
  type WithdrawSccrsModalState,
} from '@/features/withdraw-sccrs';
import { useTranslation } from 'react-i18next';
import { GamingHallLabel } from '@/entities/sccrs';
import { useAfterOperationRefresh } from '@/entities/operation';
import { useCallback } from 'react';
import { useWithdrawMFAFlow } from '@/shared/lib';
import {
  type InfoDataGridData,
  WithdrawConfirmationModal,
} from '@/shared/components';

interface Props {
  onBack: () => void;
  storeData: WithdrawSccrsModalState;
  commissionData: WithdrawCommissionData;
  continueMFA?: boolean;
}

export const WithdrawConfirmationModalContainer = ({
  onBack,
  storeData,
  commissionData,
  continueMFA,
}: Props) => {
  const { t } = useTranslation();
  const { openResultModal, openConfirmationModal } = useWithdrawSccrsModals();

  const afterOperationRefresh = useAfterOperationRefresh();

  const currencyCode = storeData.selectedCurrency.code;
  const initialParams: WithdrawSccrsBodyRequest = {
    accountNumber: storeData.accountNumber,
    amount: storeData.totalAmount.toString(),
    currencyCode,
    gamingHallId: storeData.gamingHallId,
  };

  const injectMFA = useCallback(
    (
      params: WithdrawSccrsBodyRequest,
      mfa: TwoFAParams
    ): WithdrawSccrsBodyRequest => {
      return {
        ...params,
        twoFA: mfa,
      };
    },
    []
  );

  const { showMFA, startMFAVerification, renderMFAView } = useWithdrawMFAFlow<
    WithdrawSccrsBodyRequest,
    WithdrawSuccessResponse,
    WithdrawSccrsErrorResponse
  >({
    initialParams,
    injectMFA,
    useMutationHook: useWithdrawSccrs,
    onSuccess: () => {
      openResultModal({ success: true });
      afterOperationRefresh();
    },
    onError: () => {
      openResultModal({ success: false });
    },
    onBack: openConfirmationModal,
    onClose: openConfirmationModal,
    onCancel: openConfirmationModal,
    onMFASetupComplete: () => {
      openConfirmationModal({ continueMFA: true });
    },
    modalTitle: 'withdrawMethods.crypto.confirmModal.title',
    continueMFA,
  });

  const onConfirmed = useCallback(() => {
    startMFAVerification();
  }, [startMFAVerification]);

  const amountsInfo: InfoDataGridData =
    storeData && commissionData
      ? [
          {
            key: t('withdrawMethods.sccrs.placeholders.platform'),
            value: <GamingHallLabel hall={storeData.platform} />,
          },
          {
            key: t('withdrawMethods.amountInfo.totalWithdraw'),
            value: commissionData.totalWithdrawAmountStr,
          },
          {
            key: t('withdrawMethods.amountInfo.withdrawFee'),
            value: commissionData.feeStr,
          },
          {
            key: t('withdrawMethods.amountInfo.toBeReceived'),
            value: commissionData.toBeReceivedStr,
          },
        ]
      : [];

  if (showMFA) {
    return renderMFAView();
  }

  return (
    <WithdrawConfirmationModal
      title={t('withdrawMethods.crypto.confirmModal.title')}
      onBack={onBack}
      infoData={[amountsInfo]}
      onConfirmed={onConfirmed}
      isLoading={false}
      buttonText={t('withdrawMethods.withdrawButton')}
    />
  );
};
